import { getMaterialStats } from "@/api/materialAnalysis";
import { EnhancedDataTable } from "adizen-tanstack-table";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Eye, 
  MoreHorizontal,
  Play,
  Users
} from "lucide-react";
import { authStore } from "@/stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "@/stores/permissionStore";
import { useSnapshot } from "valtio";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";

export default function MaterialAnalytics() {
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const navigate = useNavigate();
  const isAdmin = snap?.user?.role === 'ADMIN' || snap?.user?.role === 'SUPER_ADMIN';
  
  // Permission checks
  const canReadAnalytics = permSnap.can(PermissionAction.READ, ResourceType.ANALYTICS);
  const canExportAnalytics = permSnap.can(PermissionAction.EXPORT, ResourceType.ANALYTICS);

  // Must be referentially stable: the table's internal fetch effect keys off
  // this callback's identity, so a new function per render causes duplicate
  // requests.
  const fetchMaterialStats = useCallback(async (params) => {
    try {
      // Extract direct parameters from the simplified format
      const {
        page = 1,
        limit = 10,
        search,
        sortBy,
        sortOrder,
        isCompleted,
        completionPercentage,
        watchTime,
        lastAccessedDaysAgo,
        createdDaysAgo,
        dateFrom,
        dateTo,
        pauseCount,
        replayCount,
        skipCount,
        timeSpent,
        pagesViewed,
        videoCompleted,
        ...otherParams
      } = params || {};

      // Build filters object for API
      const filters = {
        page,
        limit,
        sortBy: sortBy || 'lastAccessedAt',
        sortOrder: sortOrder || 'desc',
      };

      // Add search if provided
      if (search) {
        filters.search = search;
      }

      // Handle completion filters
      if (isCompleted && isCompleted !== 'all') {
        filters.isCompleted = isCompleted === 'true';
      }

      // Handle date range filters — each bound is independent
      if (dateFrom) {
        const startOfDay = new Date(dateFrom);
        startOfDay.setHours(0, 0, 0, 0);
        filters.dateFrom = startOfDay;
      }
      if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        filters.dateTo = endOfDay;
      }

      // Handle numeric filters
      if (lastAccessedDaysAgo) {
        const startOfDay = new Date(lastAccessedDaysAgo);
        startOfDay.setHours(0, 0, 0, 0);
      
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
      
        const diffTime = currentDate.getTime() - startOfDay.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
        filters.lastAccessedDaysAgo = diffDays;
      }
      
      
      if (createdDaysAgo) {
        const selectedDate = new Date(createdDaysAgo);
        const currentDate = new Date();
      
        const diffTime = currentDate.getTime() - selectedDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); 
      
        filters.createdDaysAgo = diffDays;
      }

      // Add any other parameters that weren't explicitly handled
      Object.keys(otherParams).forEach(key => {
        if (otherParams[key] && otherParams[key] !== 'all') {
          filters[key] = otherParams[key];
        }
      });

      const response = await getMaterialStats(filters);

      if (response.success && Array.isArray(response.data.stats)) {
        const mappedStats = response.data.stats.map((stat) => ({
          id: stat.id,
          userId: stat.userId,
          materialId: stat.materialId,
          watchTime: stat.watchTime,
          lastPosition: stat.lastPosition,
          completionPercentage: stat.completionPercentage,
          isCompleted: stat.isCompleted,
          pauseCount: stat.pauseCount,
          replayCount: stat.replayCount,
          skipCount: stat.skipCount,
          pagesViewed: stat.pagesViewed,
          totalPages: stat.totalPages,
          timeSpent: stat.timeSpent,
          lastAccessedAt: stat.lastAccessedAt,
          createdAt: stat.createdAt,
          updatedAt: stat.updatedAt,
          minutesViewed: stat.minutesViewed,
          videoCompleted: stat.videoCompleted,
          date: stat.date,
          
          // User information
          userName: stat.user?.name,
          userEmail: stat.user?.email,
          userCdc: stat.user?.indianCdc,
          userAadhar: stat.user?.aadhaarNumber,
          userMobile: stat.user?.mobileNumber,
          
          // Material information
          materialTitle: stat.material?.title,
          materialDescription: stat.material?.description,
          topicName: stat.material?.topic?.name,
          courseName: stat.material?.topic?.course?.name,
          
          // Raw data for actions
          _raw: stat,
        }));

        return {
          success: true,
          message: response.message,
          data: mappedStats,
          meta: {
            total: response.data.pagination?.total || mappedStats.length,
            page: response.data.pagination?.page || page,
            limit: response.data.pagination?.limit || limit,
            totalPages: response.data.pagination?.totalPages || Math.ceil(mappedStats.length / limit),
            hasNext: response.data.pagination?.hasNext || false,
            hasPrev: response.data.pagination?.hasPrev || false,
          },
        };
      } else {
        throw new Error(response.message || 'Failed to fetch material statistics');
      }
    } catch (error) {
      console.error("Error fetching material stats:", error);
      toast.error(
        getApiErrorMessage(error, "Failed to fetch material statistics")
      );
      return {
        success: false,
        message: error.message,
        data: [],
        meta: {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }, []);

  // Built here rather than via useServerSideTableData: that hook returns a new
  // config object (and a new onDataRequest closure) on every render, which
  // re-triggers the table's internal fetch effect and produces duplicate
  // requests. A memoised config keeps the identity stable.
  const tableServerSide = useMemo(
    () => ({ enabled: true, onDataRequest: fetchMaterialStats, debounceMs: 500 }),
    [fetchMaterialStats]
  );



  // Handle actions
  const handleViewMaterial = (materialId) => {
    navigate(`/materials/${materialId}`);
  };

  const handleViewUser = (userId) => {
    navigate(`/admin/users/view/${userId}`);
  };

  const handleViewCourse = (courseId) => {
    // Extract course ID from the raw data if available
    navigate(`/courses/${courseId}`);
  };

  const columns = useMemo(() => {
    return [
      {
        id: "serialNumber",
        header: "S.No",
        accessorFn: (row, index) => index + 1,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 60,
      },
      {
        id: "userName",
        header: "User Name",
        accessorKey: "userName",
        accessorFn: (stat) => <span className="text-nowrap">{stat.userName}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userName",
        size: 150,
        cell: ({ getValue }) => {
          return <span className="font-medium">{getValue()}</span>;
        },
        exportConfig: {
          exportHeader: "User Name",
          exportValueFn: (stat) => stat.userName,
        },
      },
      {
        id: "userEmail",
        header: "User Email",
        accessorKey: "userEmail",
        accessorFn: (stat) => <span className="text-nowrap">{stat.userEmail}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userEmail",
        size: 200,
        exportConfig: {
          exportHeader: "User Email",
          exportValueFn: (stat) => stat.userEmail,
        },
      },
      {
        id: "userCdc",
        header: "INDoS Number",
        accessorKey: "userCdc",
        accessorFn: (stat) => <span className="text-nowrap">{stat.userCdc}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userCdc",
        size: 120,
        exportConfig: {
          exportHeader: "INDoS Number",
          exportValueFn: (stat) => stat.userCdc,
        },
      },
      {
        id: "userAadhar",
        header: "User Aadhar",
        accessorKey: "userAadhar",
        accessorFn: (stat) => <span className="text-nowrap">{stat.userAadhar}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userAadhar",
        size: 150,
        exportConfig: {
          exportHeader: "User Aadhar",
          exportValueFn: (stat) => stat.userAadhar,
        },
      },
      {
        id: "userMobile",
        header: "User Mobile",
        accessorKey: "userMobile",
        accessorFn: (stat) => <span className="text-nowrap">{stat.userMobile}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userMobile",
        size: 120,
        exportConfig: {
          exportHeader: "User Mobile",
          exportValueFn: (stat) => stat.userMobile,
        },
      },
      {
        id: "materialTitle",
        header: "Material Title",
        accessorKey: "materialTitle",
        accessorFn: (stat) => <span className="text-nowrap">{stat.materialTitle}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialTitle",
        size: 200,
        exportConfig: {
          exportHeader: "Material Title",
          exportValueFn: (stat) => stat.materialTitle,
        },
      },
      {
        id: "materialDescription",
        header: "Material Description",
        accessorKey: "materialDescription",
        accessorFn: (stat) => stat.materialDescription,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialDescription",
        size: 250,
        cell: ({ getValue }) => {
          const description = getValue();
          return (
            <span className="text-sm min-w-sm text-gray-600 line-clamp-2">
              {description || '-'}
            </span>
          );
        },
        exportConfig: {
          exportHeader: "Material Description",
          exportValueFn: (stat) => stat.materialDescription,
        },
      },
      {
        id: "topicName",
        header: "Topic Name",
        accessorKey: "topicName",
        accessorFn: (stat) => <span className="text-nowrap">{stat.topicName}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "topicName",
        size: 150,
        exportConfig: {
          exportHeader: "Topic Name",
          exportValueFn: (stat) => stat.topicName,
        },
      },
      {
        id: "courseName",
        header: "Course Name",
        accessorKey: "courseName",
        accessorFn: (stat) => <span className="text-nowrap">{stat.courseName}</span>,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "courseName",
        size: 150,
        exportConfig: {
          exportHeader: "Course Name",
          exportValueFn: (stat) => stat.courseName,
        },
      },
      {
        id: "isCompleted",
        header: "Completed",
        accessorKey: "isCompleted",
        accessorFn: (stat) => stat.isCompleted,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        serverFilterKey: "isCompleted",
        serverSortKey: "isCompleted",
        size: 120,
        cell: ({ getValue }) => {
          const isCompleted = getValue();
          return (
            <Badge 
              className={
                isCompleted
                  ? "bg-green-100 text-green-800" 
                  : "bg-yellow-100 text-yellow-800"
              }
            >
              {isCompleted ? 'YES' : 'NO'}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Completed",
          exportValueFn: (stat) => stat.isCompleted ? 'Yes' : 'No',
        },
      },
      {
        id: "completionPercentage",
        header: "Progress",
        accessorKey: "completionPercentage",
        accessorFn: (stat) => stat.completionPercentage,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "completionPercentage",
        serverSortKey: "completionPercentage",
        enableSorting: true,
        size: 150,
        cell: ({ getValue }) => {
          const percentage = getValue();
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium">{percentage}%</span>
              <Progress value={percentage} className="h-2 w-16" />
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Completion Percentage",
          exportValueFn: (stat) => `${stat.completionPercentage}%`,
        },
      },
      {
        id: "watchTime",
        header: "Watch Time",
        accessorKey: "watchTime",
        accessorFn: (stat) => stat.watchTime,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        enableSorting: true,
        serverFilterKey: "watchTime",
        serverSortKey: "watchTime",
        size: 120,
        cell: ({ getValue }) => {
          const time = getValue();
          return <span className="text-nowrap">{time}</span>;
        },
        exportConfig: {
          exportHeader: "Watch Time",
          exportValueFn: (stat) => stat.watchTime,
        },
      },
      {
        id: "timeSpent",
        header: "Time Spent",
        accessorKey: "timeSpent",
        accessorFn: (stat) => stat.timeSpent,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        enableSorting: true,
        serverFilterKey: "timeSpent",
        serverSortKey: "timeSpent",
        size: 120,
        cell: ({ getValue }) => {
          const time = getValue();
          return <span className="text-nowrap">{time}</span>;
        },
        exportConfig: {
          exportHeader: "Time Spent",
          exportValueFn: (stat) => stat.timeSpent,
        },
      },
      {
        id: "pauseCount",
        header: "Pauses",
        accessorKey: "pauseCount",
        accessorFn: (stat) => stat.pauseCount,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        enableSorting: true,
        serverFilterKey: "pauseCount",
        serverSortKey: "pauseCount",
        size: 80,
        cell: ({ getValue }) => {
          const count = getValue();
          return <span className="text-nowrap">{count}</span>;
        },
        exportConfig: {
          exportHeader: "Pause Count",
          exportValueFn: (stat) => stat.pauseCount,
        },
      },
      {
        id: "replayCount",
        header: "Replays",
        accessorKey: "replayCount",
        accessorFn: (stat) => stat.replayCount,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        enableSorting: true,
        serverFilterKey: "replayCount",
        serverSortKey: "replayCount",
        size: 80,
        cell: ({ getValue }) => {
          const count = getValue();
          return <span className="text-nowrap">{count}</span>;
        },
        exportConfig: {
          exportHeader: "Replay Count",
          exportValueFn: (stat) => stat.replayCount,
        },
      },
      {
        id: "skipCount",
        header: "Skips",
        accessorKey: "skipCount",
        accessorFn: (stat) => stat.skipCount,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "skipCount",
        serverSortKey: "skipCount",
        enableSorting: true,
        size: 80,
        cell: ({ getValue }) => {
          const count = getValue();
          return <span className="text-nowrap">{count}</span>;
        },
        exportConfig: {
          exportHeader: "Skip Count",
          exportValueFn: (stat) => stat.skipCount,
        },
      },
      {
        id: "pagesViewed",
        header: "Pages",
        accessorKey: "pagesViewed",
        accessorFn: (stat) => stat.pagesViewed,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "pagesViewed",
        serverSortKey: "pagesViewed",
        size: 100,
        cell: ({ getValue }) => {
          const viewed = getValue();
          return <span className="text-nowrap">{viewed || '-'}</span>;
        },
        exportConfig: {
          exportHeader: "Pages Viewed",
          exportValueFn: (stat) => stat.pagesViewed || '-',
        },
      },
      {
        id: "totalPages",
        header: "Total Pages",
        accessorKey: "totalPages",
        accessorFn: (stat) => stat.totalPages,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "totalPages",
        serverSortKey: "totalPages",
        size: 120,
        cell: ({ getValue }) => {
          const total = getValue();
          return <span className="text-nowrap">{total || '-'}</span>;
        },
        exportConfig: {
          exportHeader: "Total Pages",
          exportValueFn: (stat) => stat.totalPages || '-',
        },
      },
      {
        id: "lastAccessedAt",
        header: "Last Accessed From",
        accessorKey: "lastAccessedAt",
        accessorFn: (stat) => stat.lastAccessedAt,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "date",
        serverFilterKey: "lastAccessedDaysAgo",
        serverSortKey: "lastAccessedAt",
        enableSorting: true,
        size: 150,
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? new Date(date).toLocaleDateString() : '-';
        },
        exportConfig: {
          exportHeader: "Last Accessed",
          exportValueFn: (stat) => stat.lastAccessedAt ? new Date(stat.lastAccessedAt).toLocaleString() : '-',
        },
      },
      {
        id: "date",
        header: "Date",
        accessorKey: "date",
        accessorFn: (stat) => stat.date,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "date",
        serverFilterKey: "dateFrom",
        serverSortKey: "createdAt",
        enableSorting: true,
        size: 120,
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? new Date(date).toLocaleDateString() : '-';
        },
        exportConfig: {
          exportHeader: "Date",
          exportValueFn: (stat) => stat.date,
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "actions",
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
        cell: ({ row }) => {
          const stat = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={() => handleViewUser(stat.userId)}
                  className="cursor-pointer"
                >
                  <Users className="mr-2 h-4 w-4" />
                  <span>View User</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        exportConfig: {
          excludeFromExport: true,
        },
      },
    ];
  }, [isAdmin]);

  return (
    <div className="space-y-6">
      <div className="min-h-screen overflow-hidden font-lato">
        <div className="w-full flex flex-col gap-4 pr-4">
          <section className="w-full flex flex-col border bg-white p-4 rounded-xl shadow-xl gap-4">
            <div className="flex justify-between items-center">
              <h3 className="uppercase text-lg text-neutral-800 font-bold">
                Learning Progress Analytics
              </h3>
            </div>
            <div>
              <EnhancedDataTable
                data={[]}
                columns={columns}
                emptyMessage="No material statistics found"
                enableDateRangeFilter={canReadAnalytics}
                dateFilterField="date"
                enableGlobalSearch={canReadAnalytics}
                globalSearchPlaceholder="Search users, materials, courses..."
                enableColumnVisibility={canReadAnalytics}
                enablePagination={true}
                initialPageSize={10}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                enableCSVExport={canExportAnalytics}
                enableCustomViews={canReadAnalytics}
                viewStorageKey={"material-analytics-view"}
                onViewChange={(view) => {
                  console.log("Material analytics view changed:", view);
                }}
                serverSide={tableServerSide}
                theme="light"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}